package co.maytes.standupbot.dashboard.features.templates

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.shared.dto.template.CreateTemplateRequestDto
import co.maytes.standupbot.shared.dto.template.TemplateDto
import co.maytes.standupbot.shared.dto.template.TestFireRequestDto
import co.maytes.standupbot.shared.dto.template.TestFireResponseDto
import co.maytes.standupbot.shared.dto.template.UpdateTemplateRequestDto

enum class ListPhase { Idle, Loading, Ready, Failed }

class TemplatesStore(private val api: ApiClient) {
    var list: List<TemplateDto> by mutableStateOf(emptyList())
        private set

    var phase: ListPhase by mutableStateOf(ListPhase.Idle)
        private set

    var lastError: String? by mutableStateOf(null)
        private set

    suspend fun refresh(workspaceId: String): Result<List<TemplateDto>, ApiError> {
        phase = ListPhase.Loading
        val res = api.get<List<TemplateDto>>("/api/v1/workspaces/$workspaceId/templates")
        when (res) {
            is Result.Ok -> {
                list = res.value
                lastError = null
                phase = ListPhase.Ready
            }
            is Result.Err -> {
                lastError = res.error.displayMessage
                phase = ListPhase.Failed
            }
        }
        return res
    }

    suspend fun get(workspaceId: String, templateId: String): Result<TemplateDto, ApiError> =
        api.get("/api/v1/workspaces/$workspaceId/templates/$templateId")

    suspend fun create(
        workspaceId: String,
        body: CreateTemplateRequestDto,
    ): Result<TemplateDto, ApiError> =
        api.post("/api/v1/workspaces/$workspaceId/templates", body)

    suspend fun update(
        workspaceId: String,
        templateId: String,
        body: UpdateTemplateRequestDto,
    ): Result<TemplateDto, ApiError> =
        api.patch("/api/v1/workspaces/$workspaceId/templates/$templateId", body)

    suspend fun delete(workspaceId: String, templateId: String): Result<Unit, ApiError> =
        api.delete("/api/v1/workspaces/$workspaceId/templates/$templateId")

    suspend fun testFire(
        workspaceId: String,
        body: TestFireRequestDto,
    ): Result<TestFireResponseDto, ApiError> =
        api.post("/api/v1/workspaces/$workspaceId/templates/test-fire", body)
}

@Composable
fun rememberTemplatesStore(): TemplatesStore {
    val api = rememberApiClient()
    return remember(api) { TemplatesStore(api) }
}
